import type { ParsedAttendance, ParsedPerson, ParsedSalary } from "../types";

const FIELD_LABELS = /^(?:姓名|名字|人员|工种|电话|电话号码|联系电话|手机|手机号|身份证|身份证号|身份证号码|银行卡号|银行卡|银行账号|账号|帐号|卡号|工资卡|开户银行|开户行名称|开户行|银行名称|薪资类型|工资类型|日薪|月薪|单价|加班时薪|加班单价|加班小时单价)\s*[:：]?/;
const HOUR_NUMBER_SOURCE = String.raw`(?:\d+(?:[.．]\d+)?|半|[零〇一二两三四五六七八九十]+(?:个?半)?)`;

function numericHours(value: string) {
  const compact = value.replace(/\s/g, "").replace("．", ".");
  const arabic = Number(compact);
  if (Number.isFinite(arabic)) return arabic;
  if (compact === "半") return 0.5;
  const half = /个?半$/.test(compact);
  const integerText = compact.replace(/个?半$/, "");
  const digits: Record<string, number> = {
    零: 0, 〇: 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4,
    五: 5, 六: 6, 七: 7, 八: 8, 九: 9,
  };
  let integer = 0;
  if (integerText.includes("十")) {
    const [left, right] = integerText.split("十");
    integer = (left ? digits[left] ?? 0 : 1) * 10 + (right ? digits[right] ?? 0 : 0);
  } else if (integerText) {
    integer = digits[integerText] ?? Number.NaN;
  }
  return Number.isFinite(integer) ? integer + (half ? 0.5 : 0) : Number.NaN;
}

function clean(value: string) {
  return value.replace(/[，,；;。]+$/g, "").trim();
}

function validDate(year: number, month: number, day: number) {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function iso(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function attendanceHeading(value: string) {
  const heading = value
    .replace(/\s*考勤\s*$/, "")
    .replace(/(?:炼油分部|给排水专业)/g, "")
    .replace(/(?:蒸汽)?凝结水(?:回收管网改造)?(?:项目)?/g, "")
    .replace(/项目$/g, "")
    .replace(/\s+/g, "")
    .trim();
  const location = heading.match(
    /^(.{1,24}?(?:预制厂|罐区|车间|号路|厂|区|路|场|站|库|楼|层|线|段))/,
  )?.[1];
  if (!location) return { area: heading, workContent: "" };
  const workContent = heading
    .slice(location.length)
    .replace(/打沙/g, "打砂")
    .replace(/转管\s*打砂/g, "转管、打砂")
    .replace(/[，,；;、]+/g, "、")
    .replace(/^、+|、+$/g, "")
    .trim();
  return { area: location, workContent };
}

function attendanceAmountRegex(flags = "gi") {
  return new RegExp(
    `(?:全天|一\\s*天|1\\s*天|(?:上午|下午)?\\s*(?:半天|0[.．]5\\s*天))(?:晚上|夜间)?\\s*(?:加班\\s*${HOUR_NUMBER_SOURCE}\\s*(?:小时|时|h))?|(?:晚上|夜间)?\\s*加班\\s*${HOUR_NUMBER_SOURCE}\\s*(?:小时|时|h)|(?:正班|出勤|正常)\\s*[:：]?\\s*${HOUR_NUMBER_SOURCE}\\s*(?:小时|时|h)?|${HOUR_NUMBER_SOURCE}\\s*(?:小时|时|h)`,
    flags,
  );
}

function splitInlineAttendanceText(line: string): string[] {
  const marker = line.indexOf("考勤");
  if (marker >= 0) {
    const heading = line.slice(0, marker + 2).trim();
    const remainder = line.slice(marker + 2).trim();
    if (remainder && attendanceAmountRegex("i").test(remainder))
      return [heading, ...splitInlineAttendanceText(remainder)];
  }
  const amounts = Array.from(line.matchAll(attendanceAmountRegex()));
  if (amounts.length <= 1) return [line];
  const segments: string[] = [];
  let start = 0;
  amounts.forEach((match, index) => {
    const end = (match.index ?? 0) + match[0].length;
    if (index < amounts.length - 1) {
      const nextAmountStart = amounts[index + 1].index ?? line.length;
      const between = line.slice(end, nextAmountStart);
      const nextName = between.match(/(?:[\u3400-\u9fff]\s*){2,5}$/)?.[0];
      const splitAt = nextName ? end + between.lastIndexOf(nextName) : end;
      segments.push(line.slice(start, splitAt));
      start = splitAt;
    } else {
      segments.push(line.slice(start));
    }
  });
  return segments.map(clean).filter(Boolean);
}

export function parseAttendanceText(text: string, fallbackDate: string): ParsedAttendance[] {
  const fallback = fallbackDate.split("-").map(Number);
  let workDate = fallbackDate;
  let area = "";
  let workContent = "";
  const output: ParsedAttendance[] = [];
  const lines = text
    .replace(/\r/g, "")
    .split("\n")
    .map(clean)
    .filter(Boolean)
    .flatMap(splitInlineAttendanceText);

  for (const original of lines) {
    let line = original.replace(/[—–－]/g, "-").replace(/＋/g, "+");
    // 同时兼容“2026年7月15号”和微信群里常见的“20267月15号”。
    const dateMatch = line.match(/(?:(20\d{2})\s*(?:年\s*)?)?(\d{1,2})\s*月\s*(\d{1,2})\s*(?:日|号)?|(?:(20\d{2})\s*[/.\-])?(\d{1,2})\s*[/.\-]\s*(\d{1,2})/);
    if (dateMatch) {
      const year = Number(dateMatch[1] || dateMatch[4] || fallback[0]);
      const month = Number(dateMatch[2] || dateMatch[5]);
      const day = Number(dateMatch[3] || dateMatch[6]);
      if (validDate(year, month, day)) workDate = iso(year, month, day);
      const remainder = clean(line.replace(dateMatch[0], ""));
      if (remainder && /考勤|区域|班组|管廊|装置|罐区|施工/.test(remainder)) {
        const heading = attendanceHeading(
          remainder.replace(/^(?:区域|班组)\s*[:：]?/, ""),
        );
        area = heading.area;
        workContent = heading.workContent;
      }
      continue;
    }
    if (/^(?:项目|工程)\s*[:：]?/.test(line)) continue;
    // “凝结水3号路考勤”属于区域标题，不能按“凝结水 + 3”解析成人员。
    const isAttendanceHeading = /考勤/.test(line) && !/(?:\d+(?:\.\d+)?\s*(?:天|个工)|半天|半个工|正班|出勤|加班)/.test(line);
    if (isAttendanceHeading || /^(?:区域|班组)\s*[:：]?/.test(line)) {
      const heading = attendanceHeading(
        line.replace(/^(?:区域|班组)\s*[:：]?/, ""),
      );
      area = heading.area;
      workContent = heading.workContent;
      continue;
    }

    line = line.replace(/^\d+[.、]\s*/, "");
    const nameMatch = line.match(
      /^((?:[\u4e00-\u9fa5][ \t　]*){2,5}?)(?=\d|[、，,:：+]|上午|下午|全天|晚上|夜间|半天|正班|出勤|加班|$)/,
    );
    if (!nameMatch) continue;
    const name = nameMatch[1].replace(/\s/g, "");
    const tail = line.slice(nameMatch[0].length);
    if (/姓名|合计|项目|工程|考勤/.test(name)) continue;

    const dayMatch = tail.match(/(\d+(?:\.\d+)?)\s*(?:天|个工)/);
    const halfDayMatch = tail.match(/(?:上午|下午)?\s*(?:半天|半个工)/);
    const fullDayMatch = tail.match(/(?:全天|一\s*天|1\s*天)/);
    const halfPeriodMatch = tail.match(/(?:上午|下午)/);
    const regularMatch = tail.match(new RegExp(`(?:正班|出勤|正常)\\s*[:：]?\\s*(${HOUR_NUMBER_SOURCE})\\s*(?:小时|时|h)?`, "i"));
    const overtimeMatch = tail.match(new RegExp(`(?:加班)\\s*[:：+]?\\s*(${HOUR_NUMBER_SOURCE})\\s*(?:小时|时|h)?|(?:\\+|加)\\s*(${HOUR_NUMBER_SOURCE})\\s*(?:小时|时|h)`, "i"));
    let regularHours = fullDayMatch
      ? 8
      : dayMatch
        ? Number(dayMatch[1]) * 8
        : halfDayMatch || halfPeriodMatch
          ? 4
          : regularMatch
            ? numericHours(regularMatch[1])
            : 0;
    let overtimeHours = overtimeMatch ? numericHours(overtimeMatch[1] || overtimeMatch[2]) : 0;
    if (!dayMatch && !halfDayMatch && !regularMatch) {
      const firstNumber = tail.match(/(\d+(?:\.\d+)?)/);
      if (firstNumber && !/加班/.test(tail.slice(0, firstNumber.index))) {
        const amount = Number(firstNumber[1]);
        regularHours = amount <= 2 ? amount * 8 : amount;
      }
    }
    if (regularHours === 0 && overtimeHours === 0 && name) regularHours = 8;
    regularHours = Math.max(0, Math.min(24, regularHours));
    overtimeHours = Math.max(0, Math.min(24, overtimeHours));
    output.push({ name, workDate, area, workContent, regularHours, overtimeHours, source: original });
  }

  const unique = new Map<string, ParsedAttendance>();
  for (const item of output) unique.set(`${item.workDate}|${item.name}|${item.area.trim()}`, item);
  return [...unique.values()];
}

function field(line: string): [string, string] | null {
  const match = line.match(/^([^:：]{1,10})\s*[:：]\s*(.*)$/);
  return match ? [match[1].trim(), clean(match[2])] : null;
}

export function parsePersonText(text: string): ParsedPerson[] {
  const results: ParsedPerson[] = [];
  let current: ParsedPerson | null = null;
  const finish = () => { if (current?.name && !FIELD_LABELS.test(current.name)) results.push(current); current = null; };

  for (const raw of text.replace(/\r/g, "").split("\n")) {
    const line = clean(raw);
    if (!line) continue;
    const pair = field(line);
    const key = pair?.[0] || "";
    const value = pair?.[1] || "";
    if (/^(?:姓名|名字|人员)$/.test(key)) { finish(); current = { name: value }; continue; }

    const leading = line.match(/^([\u4e00-\u9fa5]{2,5})(?:\s+|[，,。])(?=.*(?:1\d{10}|\d{17}[\dXx]|银行卡|身份证|焊工|电工|钳工|普工))/);
    if (leading && !FIELD_LABELS.test(line)) { finish(); current = { name: leading[1] }; }
    if (!current) {
      if (/^[\u4e00-\u9fa5]{2,5}$/.test(line) && !FIELD_LABELS.test(line)) current = { name: line };
      else continue;
    }

    const phone = line.match(/(?<!\d)(1\d{10})(?!\d)/)?.[1];
    const idNo = line.replace(/\s/g, "").match(/(?<!\d)(\d{17}[\dXx])(?!\d)/)?.[1];
    const cards = line.replace(/\s/g, "").match(/(?<!\d)(\d{16,19})(?!\d)/g) || [];
    if (phone) current.phone = phone;
    if (idNo) current.id_no = idNo.toUpperCase();
    const bankCard = cards.find(x => x !== idNo && x !== phone);
    if (bankCard) current.bank_card = bankCard;
    if (/^(?:工种)$/.test(key)) current.trade = value;
    if (/^(?:开户银行|开户行名称|开户行|银行名称)$/.test(key)) current.bank_name = value;
    if (/^(?:银行卡号|银行卡|银行账号|账号|帐号|卡号|工资卡)$/.test(key)) current.bank_card = value.replace(/\s/g, "");
    if (/^(?:电话|电话号码|联系电话|手机|手机号)$/.test(key)) current.phone = value.replace(/\D/g, "");
    if (/^(?:身份证|身份证号|身份证号码)$/.test(key)) current.id_no = value.replace(/\s/g, "").toUpperCase();
    const trade = line.match(/(焊工|电工|钳工|管工|普工|架子工|起重工|管理人员)/)?.[1];
    if (trade && !current.trade) current.trade = trade;
  }
  finish();
  return [...new Map(results.map(p => [p.name, p])).values()];
}

export function parseSalaryText(text: string): ParsedSalary[] {
  const output: ParsedSalary[] = [];
  for (const raw of text.replace(/\r/g, "").split("\n")) {
    const line = clean(raw);
    if (!line || /姓名.*(?:单价|工资)|^(?:项目|工程)/.test(line)) continue;
    const name = line.match(/^([\u4e00-\u9fa5]{2,5})(?=\s|\d|[:：])/)?.[1];
    if (!name) continue;
    const salaryType: "日薪" | "月薪" = /月薪|月工资|按月/.test(line) ? "月薪" : "日薪";
    const overtime = line.match(/(?:加班时薪|加班单价|加班小时单价|加班费)\s*[:：]?\s*(\d+(?:\.\d+)?)/)?.[1];
    const rest = line.slice(name.length).replace(/加班(?:时薪|单价|小时单价|费)\s*[:：]?\s*\d+(?:\.\d+)?/g, " ");
    const rate = rest.match(/(?:日薪|月薪|工资|单价|薪资)?\s*[:：]?\s*(\d+(?:\.\d+)?)/)?.[1];
    if (rate) output.push({ name, salaryType, rate: Number(rate), overtimeRate: overtime ? Number(overtime) : undefined });
  }
  return [...new Map(output.map(p => [p.name, p])).values()];
}

export function maskId(value: string) {
  if (!value) return "—";
  return value.length > 8 ? `${value.slice(0, 4)}••••${value.slice(-4)}` : value;
}
